import { Link, useNavigate, useLocation } from "react-router-dom";
import { hasAnyRole, isModerator as isModeratorActive, getAllRoles, getActiveRole, setActiveRole } from "../utils/auth";
import { useTheme } from "../contexts/ThemeContext";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "../api/axios";

/* ------------------------------------------------------------------ */
/*  Inline SVG icons (no extra deps, crisp at any size)                */
/* ------------------------------------------------------------------ */
const Icon = ({ path, className = "w-5 h-5" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {path}
  </svg>
);

const ICONS = {
  dashboard: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></>,
  profile: <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></>,
  report: <><path d="M8 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2" /><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M8 11h8M8 15h6" /></>,
  appraisal: <><path d="M9 11l3 3 8-8" /><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" /></>,
  admin: <><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z" /></>,
  access: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3 3-5 6-5s6 2 6 5" /><circle cx="18" cy="9" r="2" /><path d="M15.5 20c.3-2 1.6-3.2 2.5-3.2s2.2 1.2 2.5 3.2" /></>,
  logs: <><path d="M4 6h16M4 12h16M4 18h10" /></>,
  database: <><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" /><path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></>,
  bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></>,
  logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></>,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,
  moon: <><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></>,
  menu: <><path d="M4 6h16M4 12h16M4 18h16" /></>,
};

/* Route -> page title shown in the top bar. */
const PAGE_TITLES = {
  "/dashboard": "Dashboard",
  "/profile": "My Profile",
  "/report": "Complete Report",
  "/appraisal": "Appraisal",
  "/admin": "Admin Panel",
  "/access": "Roles & Access",
  "/logs": "Global Logs",
  "/database": "Database",
};

const COLLEGE_NAME = "ST. VINCENT PALLOTTI COLLEGE OF ENGINEERING & TECHNOLOGY";

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();

  const isModerator = isModeratorActive();
  const isSuperAdmin = hasAnyRole("super_admin");
  const canAccessAdmin = hasAnyRole("hr", "admin", "super_admin") || isModerator;

  const email = localStorage.getItem("email") || "";
  const allRoles = getAllRoles();
  const activeRole = getActiveRole();
  const prettyRole = (r) => (r || "user").replace(/_/g, " ");

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer
  const [appraisalEnabled, setAppraisalEnabled] = useState(true);
  const notifRef = useRef(null);
  const roleRef = useRef(null);

  // Whether the global appraisal feature is enabled (super_admin toggle).
  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("token");
    if (!token) return;
    axios
      .get("/appraisal/settings", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (active) setAppraisalEnabled(res.data?.appraisal_enabled !== false);
      })
      .catch(() => {
        if (active) setAppraisalEnabled(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleSwitchRole = (role) => {
    if (role === activeRole) {
      setRoleMenuOpen(false);
      return;
    }
    if (setActiveRole(role)) {
      // Reload so every synchronous hasAnyRole/localStorage read picks up the
      // new active role and the whole app re-gates consistently.
      window.location.assign("/dashboard");
    }
  };

  /* --- nav model (role gated) --- */
  const navItems = useMemo(() => {
    const items = [{ to: "/dashboard", label: "Dashboard", icon: ICONS.dashboard }];
    // Super admin has no personal profile/report — they manage OTHERS. Hide the
    // self "Profile" and "Report" links while acting as super_admin.
    if (!isSuperAdmin) {
      items.push({ to: "/profile", label: "Profile", icon: ICONS.profile });
      items.push({ to: "/report", label: "Report", icon: ICONS.report });
      // Appraisal link only when the feature is globally enabled.
      if (appraisalEnabled) items.push({ to: "/appraisal", label: "Appraisal", icon: ICONS.appraisal });
    }
    if (canAccessAdmin) items.push({ to: "/admin", label: "Admin Panel", icon: ICONS.admin });
    if (isSuperAdmin) {
      items.push({ to: "/access", label: "Roles & Access", icon: ICONS.access });
      items.push({ to: "/logs", label: "Global Logs", icon: ICONS.logs });
      items.push({ to: "/database", label: "Database", icon: ICONS.database });
    }
    return items;
  }, [canAccessAdmin, isSuperAdmin, appraisalEnabled]);

  const pageTitle = PAGE_TITLES[location.pathname] || "FINFO";

  /* --- notifications --- */
  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get("/profile/notifications", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setNotifications(res.data.notifications);
        setUnreadCount(res.data.notifications.filter((n) => !n.is_read).length);
      }
    } catch (err) {
      console.error("Error loading notifications:", err);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      localStorage.clear();
      navigate("/");
      return;
    }
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  // Close mobile drawer whenever the route changes.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close the notifications popover on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
      if (roleRef.current && !roleRef.current.contains(e.target)) {
        setRoleMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`/profile/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking notification read:", err);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const avatarInitial = (email.trim()[0] || "U").toUpperCase();

  /* ---------------- sidebar (shared by desktop + mobile drawer) ---------------- */
  const Sidebar = (
    <div className="flex h-full flex-col bg-[#0d1b3e] text-slate-200">
      {/* Brand */}
      <Link to="/dashboard" className="flex flex-col items-center gap-3 px-4 py-6 border-b border-white/10 no-underline">
        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-md overflow-hidden">
          <img
            src="/svpcet.jpg"
            alt="St. Vincent Pallotti College of Engineering & Technology"
            className="w-full h-full object-contain"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              e.currentTarget.nextSibling.style.display = "flex";
            }}
          />
          <span
            className="w-full h-full items-center justify-center text-[#0d1b3e] font-extrabold text-lg hidden"
          >
            SVP
          </span>
        </div>
        <div className="text-center">
          <div className="text-[11px] font-semibold leading-snug text-white tracking-wide max-w-[190px]">
            {COLLEGE_NAME}
          </div>
        </div>
      </Link>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navItems.map((item) => {
          const active = location.pathname === item.to;
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium no-underline transition-colors ${
                active
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-300 hover:bg-white/10 hover:text-white"
              }`}
            >
              <Icon path={item.icon} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* User + sign out */}
      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-3 px-2 py-2 mb-1">
          <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold text-sm shrink-0">
            {avatarInitial}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">{email || "User"}</div>
            <div className="text-[11px] text-slate-400 capitalize truncate">{prettyRole(activeRole)}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-red-500/20 hover:text-red-300 transition-colors"
        >
          <Icon path={ICONS.logout} />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 transition-colors duration-300">
      {/* Desktop fixed sidebar */}
      <aside className="hidden md:block fixed inset-y-0 left-0 w-64 z-40">
        {Sidebar}
      </aside>

      {/* Mobile drawer */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-64 h-full shadow-2xl">{Sidebar}</div>
          <div className="flex-1 bg-black/50" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Content column */}
      <div className="md:pl-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between h-16 px-4 md:px-6">
            <div className="flex items-center gap-3">
              <button
                className="md:hidden p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
              >
                <Icon path={ICONS.menu} />
              </button>
              <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">{pageTitle}</h1>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                title={isDarkMode ? "Light mode" : "Dark mode"}
              >
                <Icon path={isDarkMode ? ICONS.sun : ICONS.moon} />
              </button>

              {/* Notifications */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setNotifOpen((v) => !v)}
                  className="relative p-2 rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  title="Notifications"
                >
                  <Icon path={ICONS.bell} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-hidden rounded-xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200 dark:border-slate-700 z-50">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                      <span className="text-sm font-bold text-slate-800 dark:text-slate-100">Notifications</span>
                      {unreadCount > 0 && (
                        <span className="text-xs text-slate-400">{unreadCount} unread</span>
                      )}
                    </div>
                    <div className="overflow-y-auto max-h-80">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-slate-400">No notifications</div>
                      ) : (
                        notifications.map((n) => (
                          <button
                            key={n.id}
                            onClick={() => !n.is_read && handleMarkAsRead(n.id)}
                            className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-800 transition-colors ${
                              n.is_read
                                ? "bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                : "bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-950/60"
                            }`}
                          >
                            <p className={`text-sm text-slate-700 dark:text-slate-200 ${n.is_read ? "font-normal" : "font-semibold"}`}>
                              {n.message}
                            </p>
                            <p className="text-[11px] text-slate-400 mt-0.5">
                              {new Date(n.created_at).toLocaleString()}
                            </p>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* User chip + role switcher */}
              <div className="relative pl-2 border-l border-slate-200 dark:border-slate-700" ref={roleRef}>
                <button
                  onClick={() => allRoles.length > 1 && setRoleMenuOpen((v) => !v)}
                  className={`flex items-center gap-2 rounded-lg px-1.5 py-1 transition-colors ${
                    allRoles.length > 1 ? "hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer" : "cursor-default"
                  }`}
                  title={allRoles.length > 1 ? "Switch role" : ""}
                >
                  <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold text-sm">
                    {avatarInitial}
                  </div>
                  <div className="hidden sm:block leading-tight text-left">
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 max-w-[160px] truncate">
                      {email || "User"}
                    </div>
                    <div className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
                      <span className="capitalize font-medium text-blue-600 dark:text-blue-400">{prettyRole(activeRole)}</span>
                      {allRoles.length > 1 && (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </div>
                </button>

                {roleMenuOpen && allRoles.length > 1 && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-slate-100 dark:border-slate-800">
                      <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Acting as</div>
                    </div>
                    <div className="py-1">
                      {allRoles.map((r) => {
                        const selected = r === activeRole;
                        return (
                          <button
                            key={r}
                            onClick={() => handleSwitchRole(r)}
                            className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                              selected
                                ? "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-semibold"
                                : "text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                            }`}
                          >
                            <span className="capitalize">{prettyRole(r)}</span>
                            {selected && (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main scroll area */}
        <main className="flex-1 p-4 md:p-6">
          <div className="animate-fade-in">{children}</div>
        </main>

        {/* Footer */}
        <footer className="px-6 py-4 text-center text-xs text-slate-400 border-t border-slate-200 dark:border-slate-800">
          &copy; 2026 Faculty Information System. All rights reserved.
        </footer>
      </div>
    </div>
  );
}

export default Layout;
